import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  queryCollection,
  createCalendarEvent,
  updateCalendarEvent,
  deleteCalendarEvent,
  createGalleryImage,
  updateGalleryImage,
  deleteGalleryImage,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
  uploadFile,
  getOrganization,
  CalendarEvent,
  GalleryImage,
  Announcement
} from '../lib/api';
import { invalidateCache, PORTAL_CACHE_KEYS } from '../lib/portalCache';
import { 
  Calendar as CalendarIcon,
  Images,
  Plus,
  Trash,
  PencilSimple,
  X,
  Upload,
  Check,
  MapPin,
  Clock,
  ArrowsDownUp,
  Newspaper,
  CalendarBlank,
  Eye,
  EyeSlash
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

export default function PortalContentPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('news');
  const [orgSlug, setOrgSlug] = useState<string | null>(null);
  
  // News/Announcements state
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [showAnnouncementForm, setShowAnnouncementForm] = useState(false);
  
  // Calendar state
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [showEventForm, setShowEventForm] = useState(false);
  
  // Gallery state
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [uploading, setUploading] = useState(false);

  // Helper to invalidate portal cache when content changes
  const invalidatePortalCache = () => {
    if (orgSlug) {
      invalidateCache(orgSlug);
      console.log('Portal cache invalidated for:', orgSlug);
    }
  };

  useEffect(() => {
    if (user?.organization_id) {
      loadData();
      // Load organization slug for cache invalidation
      getOrganization(user.organization_id).then(res => {
        if (res.data?.slug) setOrgSlug(res.data.slug);
      });
    }
  }, [user?.organization_id]);

  const loadData = async () => {
    if (!user?.organization_id) return;
    
    setLoading(true);
    try {
      const [news, events, images] = await Promise.all([
        queryCollection<Announcement>('announcements', { organization_id: user.organization_id }),
        queryCollection<CalendarEvent>('calendar_events', { organization_id: user.organization_id }),
        queryCollection<GalleryImage>('gallery_images', { organization_id: user.organization_id })
      ]);
      
      // Sort announcements by publish_date descending
      setAnnouncements(news.sort((a, b) => 
        new Date(b.publish_date || b.created_at || '').getTime() - new Date(a.publish_date || a.created_at || '').getTime()
      ));
      setCalendarEvents(events.sort((a, b) => 
        new Date(a.start_date || '').getTime() - new Date(b.start_date || '').getTime()
      ));
      setGalleryImages(images.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Kunde inte ladda data');
    } finally {
      setLoading(false);
    }
  };

  // Announcement handlers
  const handleSaveAnnouncement = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user?.organization_id) return;

    const formData = new FormData(e.currentTarget);
    const endDate = formData.get('end_date') as string;
    
    // Validate end date is required
    if (!endDate) {
      toast.error('Slutdatum är obligatoriskt');
      return;
    }
    
    // Validate end date is max 6 months in the future
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 6);
    if (new Date(endDate) > maxDate) {
      toast.error('Slutdatum kan max vara 6 månader framåt');
      return;
    }

    const announcementData: Partial<Announcement> = {
      title: formData.get('title') as string,
      content: formData.get('content') as string,
      category: (formData.get('category') as 'news' | 'reminder' | 'important' | 'event') || 'news',
      publish_date: formData.get('publish_date') as string || new Date().toISOString().split('T')[0],
      end_date: endDate,
      organization_id: user.organization_id,
      is_published: formData.get('is_published') === 'on',
      is_public: formData.get('is_public') === 'on'
    };

    setShowAnnouncementForm(false);
    setEditingAnnouncement(null);

    try {
      if (editingAnnouncement?.id) {
        const announcementWithId = editingAnnouncement as Announcement & { _commhubId?: string };
        const updateId = announcementWithId._commhubId || editingAnnouncement.id;
        setAnnouncements(prev => prev.map(a => 
          a.id === editingAnnouncement.id ? { ...a, ...announcementData } : a
        ));
        await updateAnnouncement(updateId, announcementData);
        toast.success('Nyhet uppdaterad');
        invalidatePortalCache();
      } else {
        await createAnnouncement(announcementData);
        toast.success('Nyhet skapad');
        invalidatePortalCache();
        loadData();
      }
    } catch (error) {
      console.error('Save announcement error:', error);
      toast.error('Kunde inte spara nyhet');
      loadData();
    }
  };

  const handleDeleteAnnouncement = async (announcement: Announcement) => {
    if (!confirm('Är du säker på att du vill ta bort denna nyhet?')) return;
    
    const announcementWithId = announcement as Announcement & { _commhubId?: string };
    const deleteId = announcementWithId._commhubId || announcement.id;
    if (!deleteId) return;
    
    const previousAnnouncements = announcements;
    setAnnouncements(prev => prev.filter(a => a.id !== announcement.id));
    
    try {
      await deleteAnnouncement(deleteId);
      toast.success('Nyhet borttagen');
      invalidatePortalCache();
    } catch (error) {
      setAnnouncements(previousAnnouncements);
      console.error('Delete announcement error:', error);
      toast.error('Kunde inte ta bort nyhet');
    }
  };

  // Get max date (6 months from now)
  const getMaxEndDate = () => {
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 6);
    return maxDate.toISOString().split('T')[0];
  };

  // Calendar handlers
  const handleSaveEvent = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user?.organization_id) return;

    const formData = new FormData(e.currentTarget);
    const eventData: Partial<CalendarEvent> = {
      title: formData.get('title') as string,
      description: formData.get('description') as string,
      start_date: formData.get('start_date') as string,
      end_date: formData.get('end_date') as string || undefined,
      location: formData.get('location') as string || undefined,
      organization_id: user.organization_id,
      is_public: formData.get('is_public') === 'on'
    };

    // Close form immediately for better UX
    setShowEventForm(false);
    setEditingEvent(null);

    try {
      if (editingEvent?.id) {
        // Optimistic update for edit
        setCalendarEvents(prev => prev.map(ev => 
          ev.id === editingEvent.id ? { ...ev, ...eventData } : ev
        ).sort((a, b) => 
          new Date(a.start_date || '').getTime() - new Date(b.start_date || '').getTime()
        ));
        await updateCalendarEvent(editingEvent.id, eventData);
        toast.success('Händelse uppdaterad');
        invalidatePortalCache();
      } else {
        // For new events, we need the server-generated ID
        await createCalendarEvent(eventData);
        toast.success('Händelse skapad');
        invalidatePortalCache();
        loadData(); // Need to get the new ID from server
      }
    } catch (error) {
      console.error('Save event error:', error);
      toast.error('Kunde inte spara händelse');
      loadData(); // Reload to get correct state
    }
  };

  const handleDeleteEvent = async (event: CalendarEvent) => {
    if (!confirm('Är du säker på att du vill ta bort denna händelse?')) return;
    
    // Use _commhubId for delete operation (CommHub's internal ID) if available
    const eventWithCommhubId = event as CalendarEvent & { _commhubId?: string };
    const deleteId = eventWithCommhubId._commhubId || event.id;
    if (!deleteId) return;
    
    // Optimistic update - remove from UI immediately
    const previousEvents = calendarEvents;
    setCalendarEvents(prev => prev.filter(e => e.id !== event.id));
    
    try {
      await deleteCalendarEvent(deleteId);
      toast.success('Händelse borttagen');
      invalidatePortalCache();
    } catch (error) {
      // Rollback on error
      setCalendarEvents(previousEvents);
      console.error('Delete event error:', error);
      toast.error('Kunde inte ta bort händelse');
    }
  };

  // Gallery handlers
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !user?.organization_id) return;

    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        // Upload file
        const result = await uploadFile(file);
        const imageUrl = result.url || result.file_url || '';
        
        if (imageUrl) {
          // Create gallery image record
          await createGalleryImage({
            url: imageUrl,
            thumbnail_url: imageUrl, // For now, use same URL
            caption: file.name.replace(/\.[^/.]+$/, ''), // Remove extension
            organization_id: user.organization_id,
            is_public: true,
            sort_order: galleryImages.length
          });
        }
      }
      
      toast.success(`${files.length} bild(er) uppladdade`);
      invalidatePortalCache();
      loadData(); // Need full reload to get new image IDs from server
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Kunde inte ladda upp bild(er)');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteImage = async (image: GalleryImage) => {
    if (!confirm('Är du säker på att du vill ta bort denna bild?')) return;
    
    // Use _commhubId for delete operation (CommHub's internal ID) if available
    const imageWithCommhubId = image as GalleryImage & { _commhubId?: string };
    const deleteId = imageWithCommhubId._commhubId || image.id;
    if (!deleteId) return;
    
    // Optimistic update - remove from UI immediately
    const previousImages = galleryImages;
    setGalleryImages(prev => prev.filter(img => img.id !== image.id));
    
    try {
      await deleteGalleryImage(deleteId);
      toast.success('Bild borttagen');
      invalidatePortalCache();
    } catch (error) {
      // Rollback on error
      setGalleryImages(previousImages);
      console.error('Delete image error:', error);
      toast.error('Kunde inte ta bort bild');
    }
  };

  const handleUpdateCaption = async (image: GalleryImage, caption: string) => {
    // Use _commhubId for update operation (CommHub's internal ID) if available
    const imageWithCommhubId = image as GalleryImage & { _commhubId?: string };
    const updateId = imageWithCommhubId._commhubId || image.id;
    if (!updateId) return;
    
    // Optimistic update - update caption immediately
    const previousImages = galleryImages;
    setGalleryImages(prev => prev.map(img => 
      img.id === image.id ? { ...img, caption } : img
    ));
    
    try {
      await updateGalleryImage(updateId, { caption });
      toast.success('Bildtext uppdaterad');
      invalidatePortalCache();
    } catch (error) {
      // Rollback on error
      setGalleryImages(previousImages);
      console.error('Update caption error:', error);
      toast.error('Kunde inte uppdatera bildtext');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-2 border-slate-300 border-t-primary rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="portal-content-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Portalinnehåll</h1>
          <p className="text-slate-500 mt-1">Hantera nyheter, kalender och bildgalleri för er portal</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="news" className="gap-2">
            <Newspaper size={16} />
            Nyheter
          </TabsTrigger>
          <TabsTrigger value="calendar" className="gap-2">
            <CalendarIcon size={16} />
            Kalender
          </TabsTrigger>
          <TabsTrigger value="gallery" className="gap-2">
            <Images size={16} />
            Bildgalleri
          </TabsTrigger>
        </TabsList>

        {/* News Tab */}
        <TabsContent value="news" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Newspaper size={20} className="text-primary" />
                    Nyheter
                  </CardTitle>
                  <CardDescription>
                    Hantera nyheter som visas på er portal. Nyheter försvinner automatiskt efter slutdatumet.
                  </CardDescription>
                </div>
                <Button 
                  onClick={() => { setEditingAnnouncement(null); setShowAnnouncementForm(true); }}
                  className="gap-2"
                >
                  <Plus size={16} />
                  Ny nyhet
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Announcement Form Modal */}
              {showAnnouncementForm && (
                <div className="mb-6 p-4 border border-slate-200 rounded-lg bg-slate-50">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-medium">
                      {editingAnnouncement ? 'Redigera nyhet' : 'Skapa ny nyhet'}
                    </h3>
                    <Button variant="ghost" size="sm" onClick={() => { setShowAnnouncementForm(false); setEditingAnnouncement(null); }}>
                      <X size={16} />
                    </Button>
                  </div>
                  <form onSubmit={handleSaveAnnouncement} className="space-y-4">
                    <div>
                      <Label htmlFor="title">Rubrik *</Label>
                      <Input 
                        id="title" 
                        name="title" 
                        required
                        defaultValue={editingAnnouncement?.title || ''}
                        placeholder="T.ex. Årsmöte 2026"
                      />
                    </div>
                    <div>
                      <Label htmlFor="content">Innehåll *</Label>
                      <Textarea 
                        id="content" 
                        name="content" 
                        required
                        rows={4}
                        defaultValue={editingAnnouncement?.content || ''}
                        placeholder="Skriv nyhetens innehåll här..."
                      />
                    </div>
                    <div>
                      <Label htmlFor="category">Kategori</Label>
                      <select 
                        id="category" 
                        name="category"
                        defaultValue={editingAnnouncement?.category || 'news'}
                        className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-md text-sm"
                      >
                        <option value="news">Nyhet</option>
                        <option value="event">Händelse</option>
                        <option value="important">Viktigt</option>
                        <option value="reminder">Påminnelse</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="publish_date">Publiceringsdatum</Label>
                        <Input 
                          id="publish_date" 
                          name="publish_date" 
                          type="date"
                          defaultValue={editingAnnouncement?.publish_date || new Date().toISOString().split('T')[0]}
                        />
                      </div>
                      <div>
                        <Label htmlFor="end_date">Slutdatum *</Label>
                        <Input 
                          id="end_date" 
                          name="end_date" 
                          type="date"
                          required
                          min={new Date().toISOString().split('T')[0]}
                          max={getMaxEndDate()}
                          defaultValue={editingAnnouncement?.end_date || ''}
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Max 6 månader framåt
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-2">
                        <Checkbox 
                          id="is_published" 
                          name="is_published"
                          defaultChecked={editingAnnouncement?.is_published ?? true}
                        />
                        <Label htmlFor="is_published" className="text-sm font-normal">Publicerad</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox 
                          id="is_public" 
                          name="is_public"
                          defaultChecked={editingAnnouncement?.is_public ?? true}
                        />
                        <Label htmlFor="is_public" className="text-sm font-normal">Synlig på publik portal</Label>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <Button type="button" variant="outline" onClick={() => { setShowAnnouncementForm(false); setEditingAnnouncement(null); }}>
                        Avbryt
                      </Button>
                      <Button type="submit">
                        <Check size={16} className="mr-1" />
                        {editingAnnouncement ? 'Spara' : 'Skapa'}
                      </Button>
                    </div>
                  </form>
                </div>
              )}

              {/* Announcements List */}
              <div className="space-y-3">
                {announcements.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">
                    Inga nyheter ännu. Klicka på &quot;Ny nyhet&quot; för att skapa en.
                  </p>
                ) : (
                  announcements.map(announcement => {
                    const isExpired = announcement.end_date && new Date(announcement.end_date) < new Date();
                    return (
                      <div 
                        key={announcement.id} 
                        className={cn(
                          "flex items-start justify-between p-4 border rounded-lg",
                          isExpired ? "bg-slate-50 border-slate-200 opacity-60" : "bg-white border-slate-200"
                        )}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium text-slate-900">{announcement.title}</h4>
                            {announcement.is_published ? (
                              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">
                                <Eye size={12} /> Publicerad
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded">
                                <EyeSlash size={12} /> Utkast
                              </span>
                            )}
                            {isExpired && (
                              <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded">
                                Utgången
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-600 line-clamp-2">{announcement.content}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <CalendarBlank size={12} />
                              Publicerad: {announcement.publish_date ? new Date(announcement.publish_date).toLocaleDateString('sv-SE') : '-'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock size={12} />
                              Slutdatum: {announcement.end_date ? new Date(announcement.end_date).toLocaleDateString('sv-SE') : '-'}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-1 ml-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setEditingAnnouncement(announcement); setShowAnnouncementForm(true); }}
                          >
                            <PencilSimple size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteAnnouncement(announcement)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash size={16} />
                          </Button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Calendar Tab */}
        <TabsContent value="calendar" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <CalendarIcon size={20} className="text-primary" />
                    Kalenderhändelser
                  </CardTitle>
                  <CardDescription>
                    Lägg till händelser som visas på er portal
                  </CardDescription>
                </div>
                <Button 
                  onClick={() => { setEditingEvent(null); setShowEventForm(true); }}
                  className="gap-2"
                >
                  <Plus size={16} />
                  Lägg till händelse
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Event Form */}
              {showEventForm && (
                <form onSubmit={handleSaveEvent} className="mb-6 p-4 bg-slate-50 rounded-lg space-y-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold">
                      {editingEvent ? 'Redigera händelse' : 'Ny händelse'}
                    </h3>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={() => { setShowEventForm(false); setEditingEvent(null); }}
                    >
                      <X size={16} />
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Label htmlFor="title">Titel *</Label>
                      <Input 
                        id="title" 
                        name="title" 
                        required
                        defaultValue={editingEvent?.title || ''}
                        placeholder="T.ex. Årsmöte 2026"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="start_date">Starttid *</Label>
                      <Input 
                        id="start_date" 
                        name="start_date" 
                        type="datetime-local"
                        required
                        defaultValue={editingEvent?.start_date?.slice(0, 16) || ''}
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="end_date">Sluttid</Label>
                      <Input 
                        id="end_date" 
                        name="end_date" 
                        type="datetime-local"
                        defaultValue={editingEvent?.end_date?.slice(0, 16) || ''}
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <Label htmlFor="location">Plats</Label>
                      <Input 
                        id="location" 
                        name="location"
                        defaultValue={editingEvent?.location || ''}
                        placeholder="T.ex. Föreningslokalen"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <Label htmlFor="description">Beskrivning</Label>
                      <Textarea 
                        id="description" 
                        name="description"
                        rows={2}
                        defaultValue={editingEvent?.description || ''}
                        placeholder="Kort beskrivning av händelsen..."
                      />
                    </div>
                    
                    <div className="md:col-span-2 flex items-center gap-2">
                      <Checkbox 
                        id="is_public" 
                        name="is_public"
                        defaultChecked={editingEvent?.is_public !== false}
                      />
                      <Label htmlFor="is_public" className="font-normal">
                        Visa publikt på portalen
                      </Label>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 pt-2">
                    <Button type="submit" className="gap-2">
                      <Check size={16} />
                      {editingEvent ? 'Spara ändringar' : 'Skapa händelse'}
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={() => { setShowEventForm(false); setEditingEvent(null); }}
                    >
                      Avbryt
                    </Button>
                  </div>
                </form>
              )}

              {/* Events List */}
              {calendarEvents.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <CalendarIcon size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Inga händelser ännu.</p>
                  <p className="text-sm mt-1">Klicka på &quot;Lägg till händelse&quot; för att skapa din första.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {calendarEvents.map((event) => {
                    const date = new Date(event.start_date || '');
                    const isPast = date < new Date();
                    
                    return (
                      <div 
                        key={event.id}
                        className={cn(
                          "flex items-center gap-4 p-4 rounded-lg border",
                          isPast ? "bg-slate-50 opacity-60" : "bg-white"
                        )}
                      >
                        <div className="w-14 h-14 rounded-lg bg-primary/10 flex flex-col items-center justify-center text-primary">
                          <span className="text-xs font-medium">
                            {date.toLocaleDateString('sv-SE', { month: 'short' }).toUpperCase()}
                          </span>
                          <span className="text-xl font-bold">{date.getDate()}</span>
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-slate-900 truncate">{event.title}</h4>
                          <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                            <span className="flex items-center gap-1">
                              <Clock size={14} />
                              {date.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            {event.location && (
                              <span className="flex items-center gap-1 truncate">
                                <MapPin size={14} />
                                {event.location}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setEditingEvent(event); setShowEventForm(true); }}
                          >
                            <PencilSimple size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteEvent(event)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash size={16} />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Gallery Tab */}
        <TabsContent value="gallery" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Images size={20} className="text-primary" />
                    Bildgalleri
                  </CardTitle>
                  <CardDescription>
                    Ladda upp bilder som visas på er portal
                  </CardDescription>
                </div>
                <div>
                  <input
                    type="file"
                    id="image-upload"
                    multiple
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageUpload}
                  />
                  <Button 
                    onClick={() => document.getElementById('image-upload')?.click()}
                    disabled={uploading}
                    className="gap-2"
                  >
                    {uploading ? (
                      <>
                        <div className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full" />
                        Laddar upp...
                      </>
                    ) : (
                      <>
                        <Upload size={16} />
                        Ladda upp bilder
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {galleryImages.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Images size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Inga bilder ännu.</p>
                  <p className="text-sm mt-1">Klicka på &quot;Ladda upp bilder&quot; för att lägga till bilder.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {galleryImages.map((image, index) => (
                    <div 
                      key={image.id}
                      className="group relative bg-slate-100 rounded-lg overflow-hidden aspect-square"
                    >
                      <img
                        src={image.thumbnail_url || image.url}
                        alt={image.caption || `Bild ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      
                      {/* Overlay with actions */}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-2">
                        <input
                          type="text"
                          defaultValue={image.caption || ''}
                          placeholder="Bildtext..."
                          className="w-full text-xs bg-white/90 rounded px-2 py-1 mb-2"
                          onBlur={(e) => handleUpdateCaption(image, e.target.value)}
                        />
                        <Button
                          variant="destructive"
                          size="sm"
                          className="w-full text-xs"
                          onClick={() => handleDeleteImage(image)}
                        >
                          <Trash size={14} className="mr-1" />
                          Ta bort
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
